import { ThemeProvider } from '@emotion/react';
import { StyledEngineProvider } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDDatePicker from '../../../../../../../BaseComponents/DatePicker/EDDatePicker';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';

import AvenyTheme, {
  AvenyThemeDark,
} from '../../../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import {
  IFieldValidations,
  IFormData,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { ObjektVyKort } from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { ObjektVyKortDel } from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKortDel';
import { aktivitetColors, themeColor } from '../../../../Utils/AktivitetTheme';

import {
  useIsExtraSmallScreen,
  useIsLargeScreen,
  useIsSmallScreen,
} from '../../../../../../../Hooks/useFullscreenView';
import { incDay, lastDayInWeek } from '../../../../../../../Utils/dateUtils';
import { dateToStr } from '../../../../../../../Utils/stringUtils';
import { useTheme } from '@mui/material';

interface IProps {
  aktivitet: IFormData;
  values: IRepeteraAktivitetValues;
  setters: IRepeteraAktivitetSetters;
  isInvalidDate: (startDateNew: string) => boolean;
  notValidDateSpan: (datumFr: string, datumTi: string) => boolean;
}

const weekOptions = [
  { name: 'Varje vecka', value: 1 },
  { name: 'Varannan vecka', value: 2 },
  { name: 'Var tredje vecka', value: 3 },
  { name: 'Var fjärde vecka', value: 4 },
  { name: 'Var femte vecka', value: 5 },
  { name: 'Var sjätte vecka', value: 6 },
];

//↓↓ A function to get which weekday is a certain date. It returns an integer (1=Monday)
export const getWeekDayName = (aktivitet: IFormData) => {
  const weekDay = new Date(aktivitet?.['FRANDATUM']).getDay();
  return weekDay;
};

interface IRepeteraAktivitetValues {
  datumFr: string;
  datumTi: string;
  weekValue: {
    name: string;
    value: number;
  } | null;
  mondayChecked: boolean;
  tuesdayChecked: boolean;
  wednesdayChecked: boolean;
  thursdayChecked: boolean;
  fridayChecked: boolean;
  saturdayChecked: boolean;
  sundayChecked: boolean;
}

interface IRepeteraAktivitetSetters {
  setDatumFr: React.Dispatch<React.SetStateAction<string>>;
  setDatumTi: React.Dispatch<React.SetStateAction<string>>;
  setWeekValue: React.Dispatch<
    React.SetStateAction<{
      name: string;
      value: number;
    } | null>
  >;
  setMondayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setTuesdayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setWednesdayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setThursdayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setFridayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setSaturdayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setSundayChecked: React.Dispatch<React.SetStateAction<boolean>>;
}

export const validateRepeteraResurser = (
  props: IRepeteraAktivitetValues
): IFieldValidations => {
  const validations: IFieldValidations = [];
  if (!props.datumFr || !props.datumTi) {
    validations.push({
      fieldName: 'datumFr',
      message: 'Datum saknas',
      type: 'Error',
    });
  }
  if (!props.weekValue) {
    validations.push({
      fieldName: 'weekValue',
      message: 'Veckor saknas',
      type: 'Error',
    });
  }
  if (
    !props.mondayChecked &&
    !props.tuesdayChecked &&
    !props.wednesdayChecked &&
    !props.thursdayChecked &&
    !props.fridayChecked &&
    !props.saturdayChecked &&
    !props.sundayChecked
  ) {
    validations.push({
      fieldName: 'mondayChecked',
      message: 'Veckodag saknas',
      type: 'Error',
    });
  }
  if (new Date(props.datumFr) > new Date(props.datumTi)) {
    validations.push({
      fieldName: 'dateSpan',
      message: 'Tilldatum måste vara senare än Fråndatum',
      type: 'Error',
    });
  }
  return validations;
};

export const useRepeteraAktivitet = (
  aktivitetWeekDay: number
): { values: IRepeteraAktivitetValues; setters: IRepeteraAktivitetSetters } => {
  const [datumFr, setDatumFr] = useState('');
  const [datumTi, setDatumTi] = useState('');
  const [weekValue, setWeekValue] = useState<{
    name: string;
    value: number;
  } | null>(weekOptions[0]);
  const [mondayChecked, setMondayChecked] = useState(aktivitetWeekDay === 1);
  const [tuesdayChecked, setTuesdayChecked] = useState(aktivitetWeekDay === 2);
  const [wednesdayChecked, setWednesdayChecked] = useState(
    aktivitetWeekDay === 3
  );
  const [thursdayChecked, setThursdayChecked] = useState(
    aktivitetWeekDay === 4
  );
  const [fridayChecked, setFridayChecked] = useState(aktivitetWeekDay === 5);
  const [saturdayChecked, setSaturdayChecked] = useState(
    aktivitetWeekDay === 6
  );
  const [sundayChecked, setSundayChecked] = useState(aktivitetWeekDay === 0);

  return {
    values: {
      datumFr,
      datumTi,
      weekValue,
      mondayChecked,
      tuesdayChecked,
      wednesdayChecked,
      thursdayChecked,
      fridayChecked,
      saturdayChecked,
      sundayChecked,
    },
    setters: {
      setDatumFr,
      setDatumTi,
      setWeekValue,
      setMondayChecked,
      setTuesdayChecked,
      setWednesdayChecked,
      setThursdayChecked,
      setFridayChecked,
      setSaturdayChecked,
      setSundayChecked,
    },
  };
};

const RepeteraAktivitetForm: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const isLargeScreen = useIsLargeScreen();
  const isSmallScreen = useIsSmallScreen();
  const theme = useTheme();
  const isExtraSmallScreen = useIsExtraSmallScreen();
  let currentWeekDay = getWeekDayName(props.aktivitet);
  useEffect(() => {
    const daySetterMap = {
      1: props.setters.setMondayChecked,
      2: props.setters.setTuesdayChecked,
      3: props.setters.setWednesdayChecked,
      4: props.setters.setThursdayChecked,
      5: props.setters.setFridayChecked,
      6: props.setters.setSaturdayChecked,
      7: props.setters.setSundayChecked,
    };
    if (currentWeekDay in daySetterMap) {
      daySetterMap[currentWeekDay as keyof typeof daySetterMap](true);
    }
    // incDay does not accept the date format output in props, so it is formatted here as new Date then reverted back
    // to the old format using dateToStr
    const initialRepetitionFirstDay = dateToStr(
      incDay(new Date(props.aktivitet['FRANDATUM']), 7)
    );
    const initialRepetitionFinalDay =
      props.values.datumTi !== ''
        ? props.values.datumTi
        : dateToStr(lastDayInWeek(new Date(initialRepetitionFirstDay)));
    props.setters.setDatumFr(initialRepetitionFirstDay);
    props.setters.setDatumTi(initialRepetitionFinalDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [aktivitetColor, prelAktivitetBackground] = aktivitetColors(
    props.aktivitet?.['_typ'],
    !!props.aktivitet?.PRELBOKAD,
    false,
    theme
  );

  const getInvalidDateMessage = (
    date: string,
    dateType: string,
    isInvalidDate: (date: string) => boolean
  ): string | undefined => {
    if (date && isInvalidDate(date)) {
      return `Ogiltigt värde "${date}" i ${dateType}`;
    }
    return undefined;
  };

  const getInvalidDateSpanMessage = () => {
    if (props.notValidDateSpan(props.values.datumFr, props.values.datumTi)) {
      return `Tilldatum måste vara senare än Fråndatum`;
    }
    return undefined;
  };

  return (
    <>
      <ObjektVyKort
        background={prelAktivitetBackground}
        backgroundColor={aktivitetColor}
      >
        <StyledEngineProvider injectFirst>
          <ThemeProvider
            theme={
              themeColor(props.aktivitet?.['_typ'])
                ? AvenyThemeDark
                : AvenyTheme
            }
          >
            <EDTypography variant="subtitle2" color="black">
              Bokningsnummer: {props.aktivitet.BOKNINGSNUMMER}
            </EDTypography>
            <EDTypography variant="h5" color="black">
              {props.aktivitet.Titel2}
            </EDTypography>
            <EDTypography variant="subtitle2" color="black">
              {props.aktivitet.DatumOchTid}
            </EDTypography>
            <EDTypography variant="subtitle2" color="black">
              Bokad av: {props.aktivitet.bokadAv}
            </EDTypography>
          </ThemeProvider>
        </StyledEngineProvider>
      </ObjektVyKort>

      {!isLargeScreen ? (
        <>
          <ObjektVyKort caption="Repetera mellan">
            <EDBox sx={{ display: 'flex' }}>
              <EDDatePicker
                fullWidth
                label="Fråndatum"
                tooltip="Repetera aktiviteter med startdatum från och med detta datum"
                value={props.values.datumFr}
                onChange={props.setters.setDatumFr}
                allowOnlyNumbers
                required
                testId={'FrånDatum'}
                error={Boolean(
                  props.values.datumFr &&
                    props.isInvalidDate(props.values.datumFr)
                )}
                helperText={getInvalidDateMessage(
                  props.values.datumFr,
                  'Fråndatum',
                  props.isInvalidDate
                )}
              />
            </EDBox>
            <EDBox sx={{ display: 'flex' }}>
              <EDDatePicker
                fullWidth
                label="Tilldatum"
                tooltip="Repetera aktiviteter med startdatum till och med detta datum"
                value={props.values.datumTi}
                onChange={props.setters.setDatumTi}
                allowOnlyNumbers
                required
                testId={'TillDatum'}
                error={
                  Boolean(
                    props.values.datumTi &&
                      props.isInvalidDate(props.values.datumTi)
                  ) ||
                  props.notValidDateSpan(
                    props.values.datumFr,
                    props.values.datumTi
                  )
                }
                helperText={
                  getInvalidDateMessage(
                    props.values.datumTi,
                    'Tilldatum',
                    props.isInvalidDate
                  ) || getInvalidDateSpanMessage()
                }
              />
            </EDBox>
          </ObjektVyKort>
          <ObjektVyKort caption="Veckor">
            <EDAutoCompleteEdit
              options={weekOptions}
              value={props.values.weekValue}
              label={'Veckor'}
              onChange={option => {
                if (!Array.isArray(option)) {
                  props.setters.setWeekValue(option);
                }
              }}
              getOptionLabel={option => option.name}
              fullWidth
              hideClear
            />
          </ObjektVyKort>
        </>
      ) : (
        <ObjektVyKortDel autoFit>
          <ObjektVyKort caption="Repetera mellan">
            <EDBox sx={{ display: 'flex' }}>
              <EDDatePicker
                fullWidth
                label="Fråndatum"
                tooltip="Repetera aktiviteten från och med detta datum"
                value={props.values.datumFr}
                onChange={props.setters.setDatumFr}
                allowOnlyNumbers
                required
                testId={'FrånDatum'}
                error={Boolean(
                  props.values.datumFr &&
                    props.isInvalidDate(props.values.datumFr)
                )}
                helperText={getInvalidDateMessage(
                  props.values.datumFr,
                  'Fråndatum',
                  props.isInvalidDate
                )}
              />
              <EDBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '25px',
                }}
              ></EDBox>
              <EDDatePicker
                fullWidth
                label="Tilldatum"
                tooltip="Repetera aktiviteten till och med detta datum"
                value={props.values.datumTi}
                onChange={props.setters.setDatumTi}
                allowOnlyNumbers
                required
                testId={'TillDatum'}
                error={
                  Boolean(
                    props.values.datumTi &&
                      props.isInvalidDate(props.values.datumTi)
                  ) ||
                  props.notValidDateSpan(
                    props.values.datumFr,
                    props.values.datumTi
                  )
                }
                helperText={
                  getInvalidDateMessage(
                    props.values.datumTi,
                    'TillDatum',
                    props.isInvalidDate
                  ) || getInvalidDateSpanMessage()
                }
              />
            </EDBox>
          </ObjektVyKort>
          <ObjektVyKort caption="Veckor">
            <EDAutoCompleteEdit
              options={weekOptions}
              value={props.values.weekValue}
              label={'Veckor'}
              onChange={option => {
                if (!Array.isArray(option)) {
                  props.setters.setWeekValue(option);
                }
              }}
              getOptionLabel={option => option.name}
              fullWidth
              hideClear
            />
          </ObjektVyKort>
        </ObjektVyKortDel>
      )}

      <ObjektVyKort caption="Veckodagar">
        {isSmallScreen && isExtraSmallScreen ? (
          <EDBox sx={{ display: 'flex', flexDirection: 'column' }}>
            <EDCheckbox
              label={'Måndag'}
              checked={props.values.mondayChecked}
              onCheck={checked => {
                props.setters.setMondayChecked(checked);
              }}
              testId={'Måndag'}
            />
            <EDCheckbox
              label={'Tisdag'}
              checked={props.values.tuesdayChecked}
              onCheck={checked => {
                props.setters.setTuesdayChecked(checked);
              }}
              testId={'Tisdag'}
            />
            <EDCheckbox
              label={'Onsdag'}
              checked={props.values.wednesdayChecked}
              onCheck={checked => {
                props.setters.setWednesdayChecked(checked);
              }}
              testId={'Onsdag'}
            />
            <EDCheckbox
              label={'Torsdag'}
              checked={props.values.thursdayChecked}
              onCheck={checked => {
                props.setters.setThursdayChecked(checked);
              }}
              testId={'Torsdag'}
            />
            <EDCheckbox
              label={'Fredag'}
              checked={props.values.fridayChecked}
              onCheck={checked => {
                props.setters.setFridayChecked(checked);
              }}
              testId={'Fredag'}
            />
            <EDCheckbox
              label={'Lördag'}
              checked={props.values.saturdayChecked}
              onCheck={checked => {
                props.setters.setSaturdayChecked(checked);
              }}
              testId={'Lördag'}
            />
            <EDCheckbox
              label={'Söndag'}
              checked={props.values.sundayChecked}
              onCheck={checked => {
                props.setters.setSundayChecked(checked);
              }}
              testId={'Söndag'}
            />
          </EDBox>
        ) : (
          <>
            <EDCheckbox
              label={'Måndag'}
              checked={props.values.mondayChecked}
              onCheck={checked => {
                props.setters.setMondayChecked(checked);
              }}
              testId={'Måndag'}
            />
            <EDCheckbox
              label={'Tisdag'}
              checked={props.values.tuesdayChecked}
              onCheck={checked => {
                props.setters.setTuesdayChecked(checked);
              }}
              testId={'Tisdag'}
            />
            <EDCheckbox
              label={'Onsdag'}
              checked={props.values.wednesdayChecked}
              onCheck={checked => {
                props.setters.setWednesdayChecked(checked);
              }}
              testId={'Onsdag'}
            />
            <EDCheckbox
              label={'Torsdag'}
              checked={props.values.thursdayChecked}
              onCheck={checked => {
                props.setters.setThursdayChecked(checked);
              }}
              testId={'Torsdag'}
            />
            <EDCheckbox
              label={'Fredag'}
              checked={props.values.fridayChecked}
              onCheck={checked => {
                props.setters.setFridayChecked(checked);
              }}
              testId={'Fredag'}
            />
            <EDCheckbox
              label={'Lördag'}
              checked={props.values.saturdayChecked}
              onCheck={checked => {
                props.setters.setSaturdayChecked(checked);
              }}
              testId={'Lördag'}
            />
            <EDCheckbox
              label={'Söndag'}
              checked={props.values.sundayChecked}
              onCheck={checked => {
                props.setters.setSundayChecked(checked);
              }}
              testId={'Söndag'}
            />
          </>
        )}
      </ObjektVyKort>
    </>
  );
};
export default RepeteraAktivitetForm;
